import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Unlock, Lock, RotateCw, Shield } from 'lucide-react';

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const DecoderTool = () => {
    const [shift, setShift] = useState(3);
    const [input, setInput] = useState("");

    // Caesar Cipher Logic
    const encrypt = (text: string, s: number) => {
        return text.toUpperCase().split('').map(char => {
            const idx = ALPHABET.indexOf(char);
            if (idx === -1) return char;
            const newIdx = (idx + s) % 26;
            return ALPHABET[newIdx];
        }).join('');
    };

    const output = encrypt(input, shift);

    // Matrix-style falling characters effect could go here, but let's stick to a solid terminal feel
    return (
        <div className="min-h-screen bg-[#060D06] flex flex-col p-8 font-mono text-green-500 relative overflow-hidden">
            
            {/* CRT Screen Effects */}
            <div className="pointer-events-none fixed inset-0 z-50 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_4px,3px_100%] z-50 pointer-events-none opacity-40"></div>
            <div className="pointer-events-none fixed inset-0 z-50 shadow-[inset_0_0_100px_rgba(0,0,0,0.9)]"></div>

            <div className="relative z-10 mb-8 flex justify-between items-center max-w-6xl mx-auto w-full">
                <Link to="/" className="flex items-center gap-2 text-green-700/80 font-bold hover:text-green-400 hover:shadow-[0_0_8px_#4ade80] transition-all px-3 py-1 border border-transparent hover:border-green-500/30 rounded">
                    <Home size={20} /> [ RETURN TO SYSTEM ]
                </Link>
                <div className="flex flex-col items-end">
                    <div className="text-green-500 animate-pulse font-bold tracking-widest text-xs">CONNECTION ESTABLISHED</div>
                    <div className="text-green-700 text-[10px] uppercase">Encrypted Protocol Active</div>
                </div>
            </div>

            <div className="relative z-10 text-center mb-12">
                <h1 className="text-5xl font-black text-green-500 mb-2 flex items-center justify-center gap-4 drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]">
                    <Shield className="text-green-400" size={48} />
                    G.O.R.I.L.L.A. DECODER
                </h1>
                <p className="text-green-600/80 tracking-widest uppercase text-sm">
                    Classified Secure Transmission Terminal
                </p>
                <div className="h-px w-64 bg-green-500/50 mx-auto mt-6 shadow-[0_0_10px_#4ade80]"></div>
            </div>

            <div className="relative z-10 flex-1 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

                {/* LEFT: Controls */}
                <div className="bg-black/80 backdrop-blur border border-green-500/30 p-8 shadow-[0_0_30px_rgba(0,255,65,0.05)] relative overflow-hidden group">
                    {/* Glowing Accent Corner */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/10 blur-2xl"></div>

                    <h3 className="font-bold text-xl text-green-400 mb-6 flex items-center gap-2 uppercase tracking-wider">
                        <RotateCw className="text-green-500 transition-transform group-hover:rotate-180 duration-1000" /> 
                        Cypher Rotation Matrix
                    </h3>

                    <div className="mb-8 p-6 bg-green-950/20 border border-green-900/50 rounded-lg flex flex-col items-center shadow-[inset_0_0_20px_rgba(0,0,0,0.5)]">
                        <input
                            type="range"
                            min="0" max="25"
                            value={shift}
                            onChange={e => setShift(Number(e.target.value))}
                            className="w-full h-1 bg-green-900 appearance-none cursor-pointer mb-6"
                            style={{
                                accentColor: '#22c55e', 
                                boxShadow: '0 0 10px rgba(34,197,94,0.5)'
                            }}
                        />
                        <div className="text-6xl font-black text-green-500 drop-shadow-[0_0_10px_rgba(34,197,94,0.8)] leading-none">{shift}</div>
                        <div className="text-xs text-green-700 uppercase mt-2">Offset Magnitude</div>
                    </div>

                    {/* Alphabet Preview */}
                    <div className="space-y-4 font-mono tracking-widest text-sm">
                        <div className="flex bg-black/50 overflow-hidden text-green-600/50 border border-green-900/30">
                            {ALPHABET.split('').map(char => (
                                <div key={char} className="flex-1 text-center py-1.5 border-r border-green-900/20 last:border-0">{char}</div>
                            ))}
                        </div>
                        <div className="flex justify-center text-green-500/40">↓</div>
                        <div className="flex bg-green-900/20 overflow-hidden text-green-400 font-bold border border-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.15)]">
                            <div className="flex w-full">
                                {ALPHABET.split('').map((_, i) => (
                                    <div key={i} className="flex-1 text-center py-1.5 border-r border-green-500/20 last:border-0 relative">
                                        {/* Highlight cursor behind the shifted letters conditionally could go here */}
                                        {ALPHABET[(i + shift) % 26]}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Input/Output */}
                <div className="space-y-8 relative z-10">
                    <div className="bg-black/80 backdrop-blur border border-green-500/30 p-8 shadow-[0_0_30px_rgba(0,255,65,0.05)] group">
                        <label className="font-bold text-green-600/80 flex items-center gap-2 mb-4 uppercase tracking-wider text-sm">
                            <Unlock size={16} /> Input Raw Signal
                        </label>
                        <div className="relative">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value.toUpperCase())}
                                className="w-full bg-black border-2 border-green-900/50 p-6 font-mono text-xl outline-none focus:border-green-500 text-green-400 font-bold uppercase transition-all focus:shadow-[0_0_20px_rgba(34,197,94,0.2)]"
                                rows={3}
                                placeholder="&gt; WAITING FOR TRANSMISSION..."
                                style={{ caretColor: '#4ade80' }} // Green terminal cursor
                            />
                            {/* Blinking block cursor substitute when empty */}
                            {!input && (
                                <div className="absolute top-6 left-6 w-3 h-5 bg-green-500 animate-pulse pointer-events-none opacity-50"></div>
                            )}
                        </div>
                    </div>

                    {/* Output Block */}
                    <div className="bg-black p-8 border-2 border-green-500 relative overflow-hidden shadow-[0_0_40px_rgba(34,197,94,0.2)] group hover:border-green-400 transition-colors">
                        <label className="font-bold text-green-500 flex items-center gap-2 mb-4 uppercase tracking-widest relative z-10">
                            <Lock size={16} className="text-green-500" /> Transformed Payload 
                            <span className="text-[10px] text-green-700 bg-green-900/30 px-2 py-0.5 rounded ml-2">ROT-{26 - shift || 26}</span>
                        </label>
                        
                        <div className="h-px w-full bg-gradient-to-r from-green-500/50 to-transparent relative z-10 mb-4"></div>

                        <div className="font-mono text-3xl text-[#00FF41] font-black break-all relative z-10 min-h-[100px] leading-relaxed drop-shadow-[0_0_8px_rgba(0,255,65,0.8)]">
                            {output || (
                                <span className="opacity-20 text-green-700 animate-pulse">
                                    [ EMPTY STREAM ]
                                </span>
                            )}
                        </div>
                        
                        {/* Terminal Scanning Line Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/5 to-transparent h-[10px] w-full animate-scan pointer-events-none" />
                        
                        {/* Static noise texture */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-screen bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiAvPgo8cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSIxIiBmaWxsPSIjMDAwIiAvPgo8cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMSIgaGVpZ2h0PSIxIiBmaWxsPSIjMDAwIiAvPgo8L3N2Zz4=')]" />
                    </div>
                </div>

            </div>
        </div>
    );
};
